import { useState, useEffect } from "react"
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom"
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth"
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore"
import { SkewLoader } from "react-spinners"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import LoginPage from "./components/LoginPage"
import AreaPage from "./components/AreaPage/AreaPage"
import OfficePage from "./components/OfficePage/OfficePage"
import AdminPage from "./components/AdminPage"
import ChangePasswordModal from "./modules/password/ChangePasswordModal"

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userArea, setUserArea] = useState("")
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("darkMode")
    return savedMode ? JSON.parse(savedMode) : false
  })
  const auth = getAuth()
  const db = getFirestore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true)
      if (user) {
        setUser(user)
        await fetchUserArea(user.email)
      } else {
        setUser(null)
        setUserArea("")
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode))
    if (darkMode) {
      document.body.classList.add("dark-mode")
    } else {
      document.body.classList.remove("dark-mode")
    }
  }, [darkMode])

  const fetchUserArea = async (email) => {
    try {
      const usersRef = collection(db, "users")
      const q = query(usersRef, where("email", "==", email))
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0]
        const userData = userDoc.data()
        const area = userData.area
        if (area) {
          setUserArea(area) // Almacenar el área con su capitalización original
        }
      } else {
        console.error("No se encontró ningún documento de usuario con ese email")
      }
    } catch (error) {
      console.error("Error al obtener el área del usuario:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (user) => {
    setLoading(true)
    setUser(user)
    await fetchUserArea(user.email)
  }

  const handleLogout = async () => {
    try {
      await signOut(auth)
      setUser(null)
      setUserArea("")
      setLoading(false)
      console.log("Sesión cerrada exitosamente")
    } catch (error) {
      console.error("Error al cerrar sesión:", error)
    }
  }

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true)
  }

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => !prevMode)
  }

  const showToast = (message, type) => {
    // Implementa aquí la lógica para mostrar notificaciones
    console.log(`${type}: ${message}`)
  }

  if (loading) {
    return (
      <div className="loading-container">
        <SkewLoader color="#1cb414" loading={loading} size={30} />
      </div>
    )
  }

  return (
    <Router>
      <div className={darkMode ? "dark-mode" : ""}>
        <AppContent
          user={user}
          userArea={userArea}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          handleChangePassword={handleChangePassword}
          setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
          showToast={showToast}
        />
      </div>
    </Router>
  )
}

function AppContent({
  user,
  userArea,
  handleLogin,
  handleLogout,
  handleChangePassword,
  setIsChangePasswordModalOpen,
  darkMode,
  toggleDarkMode,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [areaType, setAreaType] = useState(null)

  useEffect(() => {
    if (user && userArea && location.pathname === "/login") {
      if (userArea.toLowerCase() === "admin") {
        navigate("/admin")
      } else {
        // Preserve original capitalization for the URL
        navigate(`/${userArea}`)
      }
    }
  }, [user, userArea, navigate, location])

  useEffect(() => {
    const fetchAreaType = async () => {
      if (user && userArea) {
        const db = getFirestore()
        const areaControllerCollection = collection(db, "areacontroller")
        const q = query(areaControllerCollection, where("name", "==", userArea.toLowerCase()))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          const areaData = querySnapshot.docs[0].data()
          setAreaType(areaData.type)
        } else {
          console.error(`Area not found in areacontroller: ${userArea}`)
          setAreaType("area")
        }
      }
    }

    fetchAreaType()
  }, [user, userArea])

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={`/${userArea}`} replace /> : <LoginPage onLogin={handleLogin} />}
        />
        <Route
          path="/admin/*"
          element={
            user && userArea === "admin" ? (
              <AdminPage
                onLogout={handleLogout}
                onChangePassword={handleChangePassword}
                setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/:area"
          element={
            user ? (
              areaType === "area" || areaType === null ? (
                <OfficePage
                  username={user.email}
                  userArea={userArea}
                  onLogout={handleLogout}
                  onChangePassword={handleChangePassword}
                  setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
                  darkMode={darkMode}
                  toggleDarkMode={toggleDarkMode}
                />
              ) : areaType === "subarea" ? (
                <AreaPage
                  username={user.email}
                  userArea={userArea}
                  onLogout={handleLogout}
                  onChangePassword={handleChangePassword}
                  setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
                  darkMode={darkMode}
                  toggleDarkMode={toggleDarkMode}
                />
              ) : (
                <div>Loading...</div>
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
      <ToastContainer />
    </>
  )
}

export default App

